import { MouseEventHandler, PropsWithChildren, RefObject, useEffect, useRef } from 'react'
import Draggable from 'react-draggable'

import {
  ColumnKey,
  ColumnPinning,
  Extension,
  HeaderCellContext,
  ResizedColumnsParams,
} from '../../DataGrid.types'
import { useExtensions } from '../../ExtensionsContext'
import { getLeftRightVars, gridClassNames, StickyController } from '../../utils'
import * as S from './HeaderCell.styles'

const MIN_COLUMN_WIDTH = 50

interface HeaderCellProps {
  columnIndex: number
  columnKey: ColumnKey
  columnMinWidth?: string
  columnPinning: ColumnPinning
  gridId: string
  groupId?: string
  headerSpan: boolean
  isLastColumn?: boolean
  isColumnSelected?: boolean
  isResizable?: boolean
  onColumnResize: ({ columnKey, updatedColumnWidth }: ResizedColumnsParams) => void
  stickyControllerRef: RefObject<StickyController>
  withLeftBorder?: boolean
  withRightBorder?: boolean
  onClick?: MouseEventHandler<HTMLDivElement>
}

export const HeaderCell = ({
  children,
  columnIndex,
  columnKey,
  columnMinWidth,
  columnPinning,
  gridId,
  groupId,
  headerSpan,
  isLastColumn,
  isColumnSelected,
  isResizable,
  onColumnResize,
  stickyControllerRef,
  withLeftBorder,
  withRightBorder,
  onClick,
}: PropsWithChildren<HeaderCellProps>) => {
  const cellRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const stickyController = stickyControllerRef.current
    if (stickyController) {
      if (cellRef.current) {
        stickyController.registerHeaderCell(cellRef.current, columnKey, columnIndex, groupId)
      } else {
        stickyController.unregister(columnKey)
      }
    }

    return () => {
      stickyController?.unregister(columnKey)
    }
  }, [cellRef, columnKey, columnIndex, stickyControllerRef, groupId])

  const context: HeaderCellContext = {
    columnIndex,
    columnKey,
  }

  const extensions = useExtensions(gridId) as Extension<unknown>[]

  const customClassnames = extensions.map(extension =>
    extension.getCustomHeaderCellClassName?.(context),
  )

  const handleOnResize = (widthDelta: number) => {
    if (widthDelta === 0) {
      return
    }

    const updatedColumnWidth = (cellRef.current?.clientWidth ?? 0) + widthDelta

    const validColumnWidth = columnMinWidth
      ? Math.max(parseInt(columnMinWidth, 10), updatedColumnWidth)
      : Math.max(MIN_COLUMN_WIDTH, updatedColumnWidth)

    onColumnResize({ columnKey, updatedColumnWidth: validColumnWidth })
  }

  const classNames = [
    gridClassNames.headerCell,
    `${gridClassNames.headerCell}-${columnIndex}`,
    `${groupId ? gridClassNames.headerCellWithGroup : ''}`,
    `column-${columnIndex}`,
    `${isLastColumn ? 'column-last' : ''}`,
    isColumnSelected ? gridClassNames.selectedRow : '',
    ...customClassnames,
  ].join(' ')

  return (
    <S.HeaderCellRoot
      ref={cellRef}
      className={classNames}
      {...getLeftRightVars(columnPinning, stickyControllerRef, columnIndex)}
      $isFirstColumn={columnIndex === 0}
      $isResizable={isResizable}
      $isSticky={columnPinning !== ColumnPinning.None}
      $headerSpan={headerSpan}
      $noRightBorder={isLastColumn}
      $topVar={headerSpan ? undefined : stickyControllerRef.current?.getHeaderGroupsHeightVar()}
      $withLeftBorder={withLeftBorder}
      $withRightBorder={withRightBorder}
      onClick={onClick}
    >
      {children}
      {isResizable && (
        <Draggable axis="x" onStop={(_, { x }) => handleOnResize(x)} position={{ x: 0, y: 0 }}>
          <S.DragLine />
        </Draggable>
      )}
    </S.HeaderCellRoot>
  )
}
